/**
 * Architecture Decision Records published on the marketing site.
 *
 * SINGLE SOURCE OF TRUTH lives at `<repo>/docs/adr/*.md`. This module
 * pulls the raw markdown in at build time via Vite's `?raw` query so
 * there's no second copy to keep in sync. Adding a new ADR to the
 * public site is a two-line change here plus a new file in docs/adr.
 *
 * Only ADRs explicitly listed here are publicly indexed. The repo has
 * older internal-only ADRs (Cloudflare-first, D1 choice, etc.) that
 * are intentionally not surfaced — the public list is curated, not a
 * dump of the docs directory.
 */
import adr0015 from '../../../../docs/adr/0015-intent-matching-deterministic-first-llm-last.md?raw';
import adr0016 from '../../../../docs/adr/0016-voice-flow-prefill-never-auto-book.md?raw';
import adr0017 from '../../../../docs/adr/0017-voice-cost-controls-idle-timeouts-no-enumerate.md?raw';

export interface PublishedAdr {
  /** URL slug — also the docs filename minus the .md suffix. */
  slug: string;
  /** ADR number, padded — used for display and sort. */
  number: string;
  /** Title without the "ADR-NNNN: " prefix. */
  title: string;
  /** One-line summary shown on the index card. */
  summary: string;
  /** Status line (e.g. "Accepted"). */
  status: string;
  /** Raw markdown source — rendered by `<AdrMarkdown />`. */
  markdown: string;
}

interface RawEntry {
  slug: string;
  number: string;
  summary: string;
  markdown: string;
}

const RAW: RawEntry[] = [
  {
    slug: '0015-intent-matching-deterministic-first-llm-last',
    number: '0015',
    summary:
      'Cheap deterministic stages — number shortcut, exact match, substring, article-stripped equality, aliases — before a constrained-output LLM. Every stage that fires saves a turn.',
    markdown: adr0015,
  },
  {
    slug: '0016-voice-flow-prefill-never-auto-book',
    number: '0016',
    summary:
      'The voice agent never writes bookings. It pre-fills the booking form via a deep link (SMS + on-screen CTA); the caller confirms with full visual context before anything is committed.',
    markdown: adr0016,
  },
  {
    slug: '0017-voice-cost-controls-idle-timeouts-no-enumerate',
    number: '0017',
    summary:
      'Workers AI Flux STT bills by streamed audio. Two-stage idle timeout, resume-after-ack, no list-all enumeration on long sets, "say option N" framing — defensive controls that compound.',
    markdown: adr0017,
  },
];

/**
 * Lift the title from the first H1 in the markdown source. ADR files
 * follow the `# ADR-NNNN: <title>` convention, so we strip the prefix
 * for display.
 */
function extractTitle(markdown: string): string {
  const firstLine = markdown.split('\n', 1)[0] ?? '';
  const stripped = firstLine.replace(/^#\s+/, '').trim();
  return stripped.replace(/^ADR-\d+:\s*/, '');
}

/**
 * Pull the status from the standard ADR header. Tolerates either
 * `- Status: Accepted` (early ADRs) or `Status: **Accepted**` (newer
 * ones). Returns "Accepted" as the default when nothing is found.
 */
function extractStatus(markdown: string): string {
  const match = markdown.match(/^\s*-?\s*Status:\s*\*{0,2}([^*\n]+)\*{0,2}/m);
  if (!match || !match[1]) return 'Accepted';
  return match[1].trim();
}

export const ADRS: PublishedAdr[] = RAW.map((entry) => ({
  slug: entry.slug,
  number: entry.number,
  title: extractTitle(entry.markdown),
  summary: entry.summary,
  status: extractStatus(entry.markdown),
  markdown: entry.markdown,
}));

export function findAdr(slug: string): PublishedAdr | undefined {
  return ADRS.find((adr) => adr.slug === slug);
}
